import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/heroSlides';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  const go = useCallback((next: number) => {
    setIndex((next + total) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5500);
    return () => clearInterval(timer);
  }, [total]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[560px] md:h-[680px] overflow-hidden bg-foreground-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点专题轮播"
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={slide.cover}
            alt={slide.title}
            title={`${slide.title} - 搞鸡娱乐资讯网`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/45 to-foreground-950/80" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 md:px-8">
        <div key={index} className="animate-fade-up max-w-3xl w-full">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-foreground-950 text-xs font-medium mb-5">
            <i className="ri-fire-fill" />
            {heroSlides[index].tag}
          </span>
          <h2 className="font-heading text-2xl md:text-5xl font-bold text-background-50 leading-tight text-balance">
            {heroSlides[index].title}
          </h2>
          <p className="text-sm md:text-base text-background-200 mt-4 md:mt-5 max-w-xl mx-auto leading-relaxed">
            {heroSlides[index].summary}
          </p>
          <div className="flex items-center justify-center gap-3 mt-7 md:mt-9">
            <Link
              to={`/detail/${heroSlides[index].articleId}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-article-line" />
              阅读全文
            </Link>
            <Link
              to="/category/variety"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/15 backdrop-blur-sm text-background-50 text-sm font-medium border border-background-50/30 hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-tv-2-line" />
              浏览综艺
            </Link>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full items-center justify-center text-background-50 bg-foreground-950/40 hover:bg-primary-500 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full items-center justify-center text-background-50 bg-foreground-950/40 hover:bg-primary-500 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2.5">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-primary-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}